import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ChevronLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { format, shipping } = useCurrency();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, items } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="font-serif-display text-3xl mb-6">Piece not found</p>
        <Link to="/collection" className="font-mono-label text-xs uppercase tracking-luxe border-b border-white/40 pb-1">
          Back to Collection
        </Link>
      </div>
    );

  if (!product) return <div className="pt-40 pb-40 text-center text-white/40 font-mono-label uppercase tracking-luxe text-sm">Loading…</div>;

  const soldOut = !!product.sold;
  const inBag = items.some((i) => i.product_id === product.id);

  const handleAdd = () => {
    if (soldOut) {
      toast.error("This piece is sold out");
      return;
    }
    const added = addItem(product);
    toast[added ? "success" : "message"](added ? "Added to bag" : "Already in your bag");
  };

  return (
    <div className="pt-24 lg:pt-28 pb-24 max-w-[1600px] mx-auto px-6 lg:px-12">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 font-mono-label text-xs uppercase tracking-luxe text-white/50 hover:text-white mb-10"
      >
        <ChevronLeft size={14} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
        {/* Gallery */}
        <div className="flex flex-col gap-4">
          <div className="bg-neutral-900 aspect-[3/4] overflow-hidden">
            <img
              data-testid="product-main-image"
              src={mediaUrl(product.images?.[activeImg] || product.images?.[0])}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 bg-neutral-900 overflow-hidden border ${
                    activeImg === i ? "border-white" : "border-transparent"
                  }`}
                >
                  <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Buy box */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-4">{product.category}</p>
          <h1 data-testid="product-name" className="font-serif-display text-4xl lg:text-5xl font-light mb-4">
            {product.name}
          </h1>
          <p data-testid="product-price" className="font-mono-label text-xl text-white mb-8">
            {format(product.price)}
          </p>
          <p className="text-white/60 leading-relaxed mb-10 max-w-md">{product.description}</p>

          <div className="mb-8 flex items-center justify-between border-y border-white/10 py-4">
            <span className="font-mono-label text-xs uppercase tracking-luxe text-white/50">Size</span>
            <span data-testid="product-size" className="font-mono-label text-sm text-white uppercase tracking-wider">
              {product.size || "One size"}
            </span>
          </div>
          <p className="font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-8">
            One-of-one · Only one available
          </p>

          <button
            data-testid="add-to-cart-btn"
            onClick={handleAdd}
            disabled={soldOut}
            className="w-full bg-white text-black py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-black"
          >
            {soldOut ? "Sold Out" : inBag ? "In Your Bag" : "Add to Bag"}
          </button>

          <div className="mt-10 border-t border-white/10 pt-8 space-y-3 text-sm text-white/50">
            <p data-testid="promo-shipping">· Free shipping within Switzerland on orders over CHF {shipping?.chf?.free_over ?? 300}</p>
            <p data-testid="promo-authenticity">· Authenticity-checked pieces, new &amp; pre-owned</p>
            <p data-testid="promo-one-of-one">· One-of-one availability · An unexpected extra with every order</p>
          </div>
        </div>
      </div>
    </div>
  );
}
